import React, { Component } from 'react';
import styled from 'styled-components';
import theme from '../../../theme';
import Button from '../Button/Button';
import { BannerBackgroundImgDiv, MobileBannerBackgroundImgDiv } from '../shared/BannerBackgroundImgDiv';
import { trackAnalytics, getAnchorButtonUrlProps, makeExternalUrl, toSlug } from '../../../../lib/utils';
import {
  DOM_LAYOUT_LIFESTYLE_BANNER,
  DL_LIFE_STYLE_BANNER,
  DATA_TRACK_ROLE_CONTAINER,
} from '../../../../lib/constants';
import { MaxWidthLayoutDesktop } from '../../../templates/MaxWidthLayout';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import { addItemsToDataLayer, dataLayerIdGenerator, dataLayerIdGeneratorNewItem } from '../../../../lib/dataLayerUtils';
import { getExpiryTimeout, isNotExpired } from '../../../../lib/helpers';
import { getBannerTracking } from '../../../../lib/helpers/bannerTracking.helpers';
import SymbolFormat from '../shared/SymbolFormat';

const Elevate = styled.div`
  z-index: ${p => p.theme.elevation[0]}; 
`;

const BannerBackgroundImg = styled.img`
  height: 100%;
  width: 100%;
  position: absolute;
  object-fit: cover;
`;

const CenteredColumnDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const TextOverlayH2 = styled.h2`
  ${(p) => p.theme.typographyRebrand.heading['2xl']}
  max-width: 90%;
  z-index: ${p => p.theme.elevation[0]};
  color: ${p => p.hexColorForTextOverlay};
  margin: 0 ${p => p.theme.unit(5)} ${p => (p.isTextOnly ? 0 : p.theme.unit(4.5))} ${p => p.theme.unit(5)};
  text-align: center;
`;

const DesktopTextOverlayH2 = styled(TextOverlayH2)`
  ${({ textOverlayWidth }) => textOverlayWidth && `max-width: ${textOverlayWidth}%;`}
`;

const MobileTextOverlayH2 = styled(TextOverlayH2)`
`;

const BannerBackgroundImgMobile = styled.img`
  width: 100%;
  height: 100%;
  object-position: center;
  object-fit: cover;
  position: absolute;
`;

class LayoutLifestyleBanner extends Component {
  constructor(props) {
    super(props);

    // Analytics: generates IDs for items
    const itemSlug = this.props.slug ? this.props.slug : '';
    const dataLayerId = dataLayerIdGenerator(DL_LIFE_STYLE_BANNER, itemSlug);
    this.state = {
      dataLayerId,
      show: true,
    };
  }

  // Analytics: Update dataLayer with new items
  componentDidMount() {
    const itemType = DL_LIFE_STYLE_BANNER;
    const newItem = dataLayerIdGeneratorNewItem(itemType, this.props, this.state.dataLayerId);
    const items = [newItem];
    addItemsToDataLayer(items, itemType);
    if (this.props.endDate) {
      setTimeout(async () => {
        this.setState({ show: false });
      }, getExpiryTimeout(this.props.endDate));
    }
  }

  render() {
    const {
      slug,
      textOverlay,
      textOverlayWidth,
      buttonText = '',
      buttonDescriptiveText = '',
      buttonUrl,
      heroImageUrl,
      startDate,
      endDate,
      tags,
      locale,
      secondaryButtonText,
      secondaryButtonDescriptiveText,
      secondaryButtonUrl,
    } = this.props;

    const hexColorForTextOverlay = this.props.hexColorForTextOverlay || theme.c.text.primary;
    const buttonsDisplayTheme = this.props.buttonsDisplayTheme || 'light';
    const hexForBackground = this.props.backgroundColor;

    const isShowing = this.state.show;
    if (!isShowing) {
      return null;
    }

    if (startDate && endDate && !isNotExpired(this.props, 'startDate', 'endDate')) {
      return null;
    }

    const primaryButton = buttonText && buttonUrl
      ? {
        buttonText,
        accessibleLabel: buttonDescriptiveText,
        buttonUrl,
        variant: 'primary',
      }
      : null;
    const secondaryButton = secondaryButtonText && secondaryButtonUrl
      ? {
        buttonText: secondaryButtonText,
        accessibleLabel: secondaryButtonDescriptiveText,
        buttonUrl: secondaryButtonUrl,
        variant: 'secondary',
      }
      : null;
    const buttonToUse = primaryButton || secondaryButton;

    const imgSrc = makeExternalUrl(heroImageUrl);
    const {
      url: resolvedUrl,
      directLink,
    } = getAnchorButtonUrlProps(
      (buttonToUse && buttonToUse.buttonUrl) || '',
      locale,
    );

    const buttonProps = buttonToUse && {
      buttonText: buttonToUse.buttonText,
      accessibleLabel: buttonToUse.accessibleLabel,
      variant: buttonToUse.variant,
      directLink,
      ...trackAnalytics(
        DOM_LAYOUT_LIFESTYLE_BANNER,
        `${slug}-${DOM_LAYOUT_LIFESTYLE_BANNER}`,
        resolvedUrl,
        tags,
      ),
      size: 'medium',
      theme: buttonsDisplayTheme,
      target: directLink && '_blank',
      dataLayerId: this.state.dataLayerId,
      bannerTracking: getBannerTracking({
        bannerImgUrl: heroImageUrl,
        bannerLocation: DOM_LAYOUT_LIFESTYLE_BANNER,
        linkName: toSlug(buttonToUse.buttonText),
      }),
    };

    const DesktopBody = () => (
      <MaxWidthLayoutDesktop>
        <BannerBackgroundImgDiv
          data-track-role={DATA_TRACK_ROLE_CONTAINER}
          data-layer-source-id={this.state.dataLayerId}
          hexForBackground={hexForBackground}>
          <BannerBackgroundImg src={imgSrc} alt="" />
          <CenteredColumnDiv>
            <DesktopTextOverlayH2
              isTextOnly={!buttonProps}
              hexColorForTextOverlay={hexColorForTextOverlay}
              textOverlayWidth={textOverlayWidth}
            >
              <SymbolFormat text={textOverlay} />
            </DesktopTextOverlayH2>
            <Elevate>
              {buttonProps && (
                <Button {...buttonProps} />
              )}
            </Elevate>
          </CenteredColumnDiv>
        </BannerBackgroundImgDiv>
      </MaxWidthLayoutDesktop>
    );

    const MobileBody = () => (
      <MobileBannerBackgroundImgDiv
        data-track-role={DATA_TRACK_ROLE_CONTAINER}
        data-layer-source-id={this.state.dataLayerId}
        hexForBackground={hexForBackground}>
        <BannerBackgroundImgMobile src={imgSrc} alt="" />
        <CenteredColumnDiv>
          <MobileTextOverlayH2 isTextOnly={!buttonProps} hexColorForTextOverlay={hexColorForTextOverlay}>
            <SymbolFormat text={textOverlay} />
          </MobileTextOverlayH2>
          <Elevate>
            {buttonProps && (
              <Button {...buttonProps} />
            )}
          </Elevate>
        </CenteredColumnDiv>
      </MobileBannerBackgroundImgDiv>
    );

    const renderComp = (
      <MediaQueryHelper
        desktopChildren={DesktopBody()}
        mobileChildren={MobileBody()} />);
    return renderComp;
  }
}

export default LayoutLifestyleBanner;
